// Scanner QR haute sensibilité partagé (portail, cantine…)
// Optimisé pour les badges imprimés en petit format (QR 14-16 mm) :
// - Détecteur natif du navigateur (BarcodeDetector) si disponible
// - Flux caméra HD (1920x1080 idéal) pour résoudre les petits modules
// - Zone de scan adaptative (80 % du cadre) au lieu d'une boîte fixe 240 px
// - Zoom optique x2 appliqué automatiquement si la caméra le supporte
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const INSTANCE_CONFIG = {
  formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
  useBarCodeDetectorIfSupported: true,
  experimentalFeatures: { useBarCodeDetectorIfSupported: true },
  verbose: false,
};

const SCAN_CONFIG = {
  fps: 15,
  qrbox: (vw, vh) => {
    const size = Math.max(200, Math.floor(Math.min(vw, vh) * 0.8));
    return { width: size, height: size };
  },
  videoConstraints: {
    facingMode: "environment",
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
};

const applyZoomAndFocus = async (qr) => {
  // Zoom x2 + mise au point continue → petits QR lisibles à ~10 cm (best effort)
  try {
    const caps = qr.getRunningTrackCapabilities();
    const advanced = [];
    if (caps?.zoom) advanced.push({ zoom: Math.min(2, caps.zoom.max || 2) });
    if (caps?.focusMode?.includes?.("continuous")) advanced.push({ focusMode: "continuous" });
    if (advanced.length) await qr.applyVideoConstraints({ advanced });
  } catch { /* capacités non supportées : on garde le flux par défaut */ }
};

/**
 * Démarre le scanner dans l'élément #elementId et appelle onDecoded(texte).
 * Retourne l'instance Html5Qrcode (à passer à stopQrScanner).
 * Lève une erreur avec un message utilisateur en français si la caméra est indisponible.
 */
export const startQrScanner = async (elementId, onDecoded) => {
  if (!window.isSecureContext) {
    throw new Error("La caméra nécessite une connexion sécurisée (HTTPS). Utilisez la saisie manuelle.");
  }
  const qr = new Html5Qrcode(elementId, INSTANCE_CONFIG);
  try {
    // 1) Caméra arrière (mobile)
    await qr.start({ facingMode: "environment" }, SCAN_CONFIG, onDecoded);
  } catch (e1) {
    // 2) Fallback : énumérer les caméras (PC / webcam)
    let cams;
    try {
      cams = await Html5Qrcode.getCameras();
    } catch (e2) {
      throw _friendlyError(e2);
    }
    if (!cams || cams.length === 0) {
      throw new Error("Aucune caméra détectée sur cet appareil. Utilisez la saisie manuelle.");
    }
    const back = cams.find((c) => /back|rear|arrière|environment/i.test(c.label || "")) || cams[0];
    try {
      await qr.start(back.id, SCAN_CONFIG, onDecoded);
    } catch (e3) {
      throw _friendlyError(e3);
    }
  }
  await applyZoomAndFocus(qr);
  return qr;
};

export const stopQrScanner = async (qr) => {
  try { await qr?.stop(); qr?.clear(); } catch { /* déjà arrêté */ }
};

const _friendlyError = (e) => {
  const msg = String(e?.message || e?.name || e || "");
  if (/NotAllowed|Permission|denied|refus/i.test(msg)) {
    return new Error("Accès caméra REFUSÉ. Cliquez sur l'icône cadenas/caméra dans la barre d'adresse du navigateur, autorisez la caméra, puis réessayez.");
  }
  if (/NotFound|Aucune caméra|Requested device not found/i.test(msg)) {
    return new Error("Aucune caméra détectée sur cet appareil. Utilisez la saisie manuelle.");
  }
  if (/NotReadable|in use|TrackStart/i.test(msg)) {
    return new Error("La caméra est utilisée par une autre application. Fermez-la puis réessayez.");
  }
  return new Error("Caméra indisponible : " + msg + ". Utilisez la saisie manuelle.");
};
